"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserPlus, Building2, TrendingUp, MapPin, CalendarCheck, CircleCheck as CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const impacts = [{
  id: "doctors",
  title: "2 Additional Doctors Onboarded",
  category: "Capacity Expansion",
  description: "A surge in targeted, high-intent private patient inquiries created immediate clinical demand, prompting the practice to recruit two new full-time specialist physicians to handle appointment volume.",
  iconName: "UserPlus",
  statValue: "+2",
  statLabel: "Specialist Doctors"
}, {
  id: "clinics",
  title: "Expansion to Satellite Clinics",
  category: "Geographic Growth",
  description: "Dominating high-value regional healthcare queries across neighboring catchment territories directly facilitated the planned rollout and sustainable filling of two new satellite clinic locations.",
  iconName: "Building2",
  statValue: "2 New",
  statLabel: "Satellite Locations"
}, {
  id: "bookings",
  title: "Qualified Inbound Inquiries",
  category: "Patient Acquisition",
  description: "By positioning the clinic's core treatments at the top of organic search and local maps, booking conversions grew sustainably without reliance on volatile paid ad budgets.",
  iconName: "CalendarCheck",
  statValue: "100%",
  statLabel: "Organic Inquiry Growth"
}];
const highlights = [{
  text: "Hands-on execution directly with me, not junior account handlers"
}, {
  text: "Targeted local search intent capturing high-value private consultations"
}, {
  text: "Sustainable revenue growth leading to physical clinic expansion"
}, {
  text: "Transparent tracking focused on bookings rather than vanity metrics"
}];
export default function Impact() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="businessimpactgrowth" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold tracking-wide uppercase">
            <Sparkles className="size-3.5 text-accent mr-1 inline" />
            Tangible Business Impact
          </Badge>

          <h2 className="font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Search Visibility That Fuels Real Commercial Growth
          </h2>

          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Organic search visibility directly fueled commercial scale for this regional
            dermatology practice—driving high-intent private patient bookings that prompted hiring
            two additional specialist doctors and opening two new satellite clinics.
          </p>
        </div>

        {/* Featured Case Highlight */}
        <div className="mt-14 lg:mt-16">
          <Card className="border-border bg-card shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 sm:p-8 lg:p-10">
              {/* Narrative Left */}
              <div className="lg:col-span-7 flex flex-col justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <Badge variant="outline" className="text-primary border-primary/30">
                      Medical & Healthcare Practice Case
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="size-3 text-accent" /> UK Regional Growth
                    </span>
                  </div>

                  <h3 className="font-default text-2xl font-bold text-card-foreground sm:text-3xl leading-snug">
                    Meeting Patient Influx: From Scaled Search Demand to Physical Clinic Expansion
                  </h3>

                  <p className="mt-4 text-sm text-muted-foreground sm:text-base leading-relaxed">
                    By overhauling technical health, revamping localized intent architecture,
                    and building high-converting landing pages for high-value treatments, we transformed
                    the clinic’s organic acquisition funnel. The surge in qualified consultation bookings
                    necessitated recruiting two additional doctors and justified expanding into dedicated
                    satellite facilities.
                  </p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {highlights.map((item, i) => <div key={item.text} data-index={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-card-foreground font-medium">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item.text}</span>
                      </div>)}
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                      Commercial Impact
                    </p>
                    <p className="text-sm font-medium text-card-foreground">
                      “Targeted search visibility translated directly into filled consultation calendars, requiring two new specialist doctors and two satellite clinics to meet demand.”
                    </p>
                  </div>
                  <Button asChild variant="default" size="default">
                    <Link to="/CaseStudiesPage" className="gap-2 font-medium">
                      Explore Case Studies
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Verified Metrics Right */}
              <div className="lg:col-span-5 flex flex-col justify-center gap-4 bg-muted/40 p-6 rounded-xl border border-border/80">
                <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
                  <TrendingUp className="size-4" />
                  Verified Growth Milestones
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-background rounded-lg p-5 border border-border shadow-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-md bg-secondary text-primary">
                          <UserPlus className="size-5" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground font-medium">Team Scale</p>
                          <h4 className="font-default text-base font-semibold text-foreground">
                            2 New Doctors Hired
                          </h4>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-primary">+2</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Surging consultation inquiries required expanding clinical staff.
                    </p>
                  </div>

                  <div className="bg-background rounded-lg p-5 border border-border shadow-xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-md bg-secondary text-accent">
                          <Building2 className="size-5" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground font-medium">Facilities</p>
                          <h4 className="font-default text-base font-semibold text-foreground">
                            Satellite Locations Opened
                          </h4>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-primary">2</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Regional dominance unlocked immediate demand in adjacent territory branches.
                    </p>
                  </div>
                </div>

                <div className="bg-background/80 rounded-lg p-4 border border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Strategy Lead:</span>
                  <span className="font-semibold text-foreground">Yassine Essaadi</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* 3 Impact Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {impacts.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="flex">
              <Card className="border-border bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-sm flex flex-col justify-between w-full">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="p-2.5 rounded-md bg-muted text-primary inline-flex">
                      {item.iconName === "UserPlus" && <UserPlus className="size-5" />}
                      {item.iconName === "Building2" && <Building2 className="size-5" />}
                      {item.iconName === "CalendarCheck" && <CalendarCheck className="size-5" />}
                    </div>
                    <Badge variant="secondary" className="text-2xs font-semibold">
                      {item.category}
                    </Badge>
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground mt-1">
                      {item.statLabel}: <span className="font-semibold text-primary">{item.statValue}</span>
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom CTA Band */}
        <div className="mt-14 rounded-xl border border-border bg-muted/40 p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h4 className="font-default text-lg font-bold text-foreground">
              Ready to scale your organic inquiries and physical capacity?
            </h4>
            <p className="text-sm text-muted-foreground">
              Book a 1-on-1 discovery call directly with me to assess your search opportunities.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="default" size="lg">
              <Link to="/ContactPage" className="font-semibold">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/ServicesPage" className="font-medium">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}